<template>
  <div class="agent-management">
    <div class="toolbar">
      <el-button type="primary" @click="showAddDialog">添加智能体</el-button>
      <el-input
        v-model="searchKeyword"
        placeholder="搜索名称或描述"
        clearable
        style="width: 300px; margin-left: 10px;"
        @input="handleSearch"
      >
        <template #prefix>
          <el-icon><Search /></el-icon>
        </template>
      </el-input>
      <el-select
        v-model="selectedCategory"
        placeholder="筛选分类"
        clearable
        style="width: 150px; margin-left: 10px;"
        @change="handleFilterChange"
      >
        <el-option
          v-for="category in categories"
          :key="category"
          :label="category"
          :value="category"
        />
      </el-select>
    </div>

    <el-table :data="displayItems" border v-loading="loading">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="name" label="名称">
        <template #default="{ row }">
          <span style="display: flex; align-items: center; gap: 8px;">
            <el-tag v-if="row.is_pinned" type="warning" size="small">置顶</el-tag>
            <span>{{ row.name }}</span>
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="category" label="分类" width="120">
        <template #default="{ row }">
          <el-tag v-if="row.category" type="info" size="small">{{ row.category }}</el-tag>
          <span v-else style="color: rgba(255, 255, 255, 0.5);">-</span>
        </template>
      </el-table-column>
      <el-table-column prop="iframe_url" label="iframe链接" width="300">
        <template #default="{ row }">
          <div class="text-ellipsis" :title="row.iframe_url">{{ row.iframe_url }}</div>
        </template>
      </el-table-column>
      <el-table-column prop="description" label="描述" width="200">
        <template #default="{ row }">
          <div class="text-ellipsis" :title="row.description">{{ row.description || '-' }}</div>
        </template>
      </el-table-column>
      <el-table-column prop="is_pinned" label="置顶" width="100">
        <template #default="{ row }">
          <el-switch
            v-model="row.is_pinned"
            @change="togglePinned(row)"
            active-text="置顶"
            inactive-text=""
          />
        </template>
      </el-table-column>
      <el-table-column prop="sort_order" label="排序权重" width="100" sortable />
      <el-table-column prop="is_active" label="启用" width="100">
        <template #default="{ row }">
          <el-switch
            v-model="row.is_active"
            @change="toggleStatus(row)"
            active-text="启用"
            inactive-text=""
          />
        </template>
      </el-table-column>
      <el-table-column prop="created_at" label="创建时间" width="180">
        <template #default="{ row }">
          {{ formatDate(row.created_at) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200">
        <template #default="{ row }">
          <el-button size="small" @click="editItem(row)">编辑</el-button>
          <el-button size="small" type="danger" @click="deleteItem(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="total > 0"
      :current-page="currentPage"
      :page-sizes="[20, 50, 100, { value: -1, label: '全部' }]"
      :page-size="pageSize"
      :total="total"
      layout="total, sizes, prev, pager, next, jumper"
      @size-change="handleSizeChange"
      @current-change="handlePageChange"
      class="pagination"
    />

    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="800px">
      <el-form :model="form" label-width="120px">
        <el-form-item label="名称" required>
          <el-input v-model="form.name" />
        </el-form-item>
        <el-form-item label="iframe链接" required>
          <el-input v-model="form.iframe_url" placeholder="例如：http://192.168.80.4:8080/chat/f1462030cce26e6d" />
          <div class="form-tip">智能体的iframe嵌入链接</div>
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="form.description" type="textarea" :rows="3" />
        </el-form-item>
        <el-form-item label="分类">
          <el-input v-model="form.category" placeholder="如：客服、助手、创作等" />
        </el-form-item>
        <el-form-item label="置顶">
          <el-switch v-model="form.is_pinned" active-text="置顶" inactive-text="" />
          <div class="form-tip">置顶的智能体会优先显示在列表前面</div>
        </el-form-item>
        <el-form-item label="排序权重">
          <el-input-number v-model="form.sort_order" :min="0" />
          <div class="form-tip">数字越大越靠前</div>
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="form.is_active" active-text="启用" inactive-text="" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveItem">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import api from '../../api'
import { Search } from '@element-plus/icons-vue'

export default {
  name: 'AgentManagement',
  components: {
    Search
  },
  data() {
    return {
      items: [],
      categories: [],
      searchKeyword: '',
      selectedCategory: null,
      currentPage: 1,
      pageSize: 20,
      total: 0,
      loading: false,
      dialogVisible: false,
      dialogTitle: '添加智能体',
      form: {
        name: '',
        iframe_url: '',
        description: '',
        category: '',
        sort_order: 0,
        is_active: true
      },
      currentId: null
    }
  },
  computed: {
    displayItems() {
      return this.items
    }
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    async loadItems() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          page_size: this.pageSize === -1 ? 'all' : this.pageSize
        }
        
        if (this.selectedCategory) {
          params.category = this.selectedCategory
        }
        
        if (this.searchKeyword) {
          params.search = this.searchKeyword
        }
        
        const response = await api.get('/admin/agents/items/', { params })
        
        if (Array.isArray(response.data)) {
          this.items = response.data
          this.total = response.data.length
        } else {
          this.items = response.data.results || []
          this.total = response.data.count || 0
        }
        
        // 提取所有分类
        const categorySet = new Set()
        this.items.forEach(item => {
          if (item.category) {
            categorySet.add(item.category)
          }
        })
        this.categories = Array.from(categorySet).sort()
      } catch (error) {
        this.$message.error('加载失败')
        this.items = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    showAddDialog() {
      this.dialogTitle = '添加智能体'
      this.currentId = null
      this.form = {
        name: '',
        iframe_url: '',
        description: '',
        category: '',
        sort_order: 0,
        is_pinned: false,
        is_active: true
      }
      this.dialogVisible = true
    },
    editItem(item) {
      this.dialogTitle = '编辑智能体'
      this.currentId = item.id
      this.form = {
        name: item.name,
        iframe_url: item.iframe_url,
        description: item.description || '',
        category: item.category || '',
        sort_order: item.sort_order || 0,
        is_pinned: item.is_pinned || false,
        is_active: item.is_active !== undefined ? item.is_active : true
      }
      this.dialogVisible = true
    },
    async deleteItem(item) {
      try {
        await this.$confirm(`确定要删除智能体"${item.name}"吗？`, '提示', {
          type: 'warning'
        })
        await api.delete(`/admin/agents/items/${item.id}/`)
        this.$message.success('删除成功')
        this.loadItems()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },
    async saveItem() {
      try {
        if (!this.form.name) {
          this.$message.warning('请输入名称')
          return
        }
        if (!this.form.iframe_url) {
          this.$message.warning('请输入iframe链接')
          return
        }
        
        if (this.currentId) {
          await api.patch(`/admin/agents/items/${this.currentId}/`, this.form)
          this.$message.success('更新成功')
        } else {
          await api.post('/admin/agents/items/', this.form)
          this.$message.success('添加成功')
        }
        this.dialogVisible = false
        this.loadItems()
      } catch (error) {
        console.error('保存失败:', error)
        const errorMsg = error.response?.data?.detail || error.response?.data?.error || '保存失败'
        this.$message.error(errorMsg)
      }
    },
    async toggleStatus(item) {
      try {
        await api.patch(`/admin/agents/items/${item.id}/`, {
          is_active: item.is_active
        })
        this.$message.success(item.is_active ? '已启用' : '已禁用')
      } catch (error) {
        item.is_active = !item.is_active
        this.$message.error('状态更新失败')
        console.error(error)
      }
    },
    async togglePinned(item) {
      try {
        await api.patch(`/admin/agents/items/${item.id}/`, {
          is_pinned: item.is_pinned
        })
        this.$message.success(item.is_pinned ? '已置顶' : '已取消置顶')
        this.loadItems() // 重新加载以更新排序
      } catch (error) {
        // 恢复原状态
        item.is_pinned = !item.is_pinned
        this.$message.error('置顶状态更新失败')
        console.error(error)
      }
    },
    handleSearch() {
      this.currentPage = 1
      this.loadItems()
    },
    handleFilterChange() {
      this.currentPage = 1
      this.loadItems()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.loadItems()
    },
    handlePageChange(page) {
      this.currentPage = page
      if (this.pageSize !== -1) {
        this.loadItems()
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.agent-management {
  width: 100%;
  color: #e0e0e0;
}

.toolbar {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.text-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.form-tip {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 5px;
}
</style>

